import { useEffect, useState } from "react";
import { api } from "../api/client";

interface NotificationEmail {
  notification_email: string | null;
  suggested: string | null;
}

/** The "Notifications" page inside Clerk's "Manage account" panel: where the
 *  user sets the email that upload-finished (and future) notices go to — set
 *  here so it doesn't depend on the identity provider exposing an address.
 *
 *  Deliberately self-contained — plain api() + useState, no React Query / app
 *  context — so it renders correctly inside Clerk's modal. api() reads the Clerk
 *  session token from the global ClerkJS instance, so auth still works here. */
export function NotificationSettings() {
  const [email, setEmail] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<NotificationEmail>("/api/me/notification-email")
      .then((d) => {
        if (cancelled) return;
        setEmail(d.notification_email ?? d.suggested ?? "");
        setPrefilled(d.notification_email == null && !!d.suggested);
      })
      .catch(() => {/* leave blank — the user can still type one */})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function save() {
    setSaving(true);
    setStatus(null);
    try {
      await api("/api/me/notification-email", { method: "PUT", body: JSON.stringify({ email: email.trim() }) });
      setPrefilled(false);
      setStatus({ kind: "ok", text: email.trim() ? "Notification email saved." : "Email notices turned off." });
    } catch (e: any) {
      setStatus({ kind: "err", text: e?.message || "Couldn't save — check the address." });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="clerk-notif">
      <header className="clerk-notif-head">
        <h1>Notifications</h1>
        <p>Choose where Mockingbird sends your notices.</p>
      </header>
      <label className="clerk-notif-field">
        <span>Notification email</span>
        <input
          className="input"
          type="email"
          placeholder="you@example.com"
          value={email}
          disabled={loading}
          onChange={(e) => { setEmail(e.target.value); setStatus(null); }}
        />
        <small>
          Where we send notices like “your upload has finished.” Leave it blank to turn
          email notices off.
          {prefilled && " (Pre-filled from your sign-in email — edit it if you’d like notices elsewhere.)"}
        </small>
      </label>
      <div className="clerk-notif-foot">
        {status && <span className={`clerk-notif-status ${status.kind}`}>{status.text}</span>}
        <button className="btn primary" disabled={saving || loading} onClick={save}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
